import { useState, useEffect, memo } from 'react';

// 3D Blocky Roblox-Style Character Component
// Renders characters with cubic/blocky shapes, flat shading, and visible face planes
const BlockyCharacter3D = memo(({ 
  skinTone = '#FFE0BD',
  hairColor = '#4a3728',
  hairStyle = 'short',
  shirtColor = '#4A90D9',
  pantsColor = '#3B82F6',
  shoes = 'trainers',
  eyeStyle = 'normal',
  mouthStyle = 'smile',
  accessory = 'none',
  size = 100,
  direction = 'down',
  isMoving = false,
  showNameTag = false,
  name = '',
  showSpeechBubble = false,
}) => {
  const isBack = direction === 'up';
  const isSide = direction === 'left' || direction === 'right';
  const isRight = direction === 'right';
  
  // Calculate colors for 3D cube faces (top lighter, side darker, front normal)
  const skinTop = adjustColor(skinTone, 15);
  const skinSide = adjustColor(skinTone, -25);
  const skinFront = skinTone;
  
  const shirtTop = adjustColor(shirtColor, 12);
  const shirtSide = adjustColor(shirtColor, -30);
  const shirtFront = shirtColor;
  
  const pantsTop = adjustColor(pantsColor, 10);
  const pantsSide = adjustColor(pantsColor, -30);
  const pantsFront = pantsColor;
  
  const hairTop = adjustColor(hairColor, 10);
  const hairSide = adjustColor(hairColor, -25);
  const hairFront = hairColor;
  
  // Animation frame for walking
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    if (!isMoving) return;
    const interval = setInterval(() => setFrame(f => (f + 1) % 4), 120);
    return () => clearInterval(interval);
  }, [isMoving]);
  
  const legAngle = isMoving ? Math.sin(frame * Math.PI / 2) * 20 : 0;
  const armAngle = isMoving ? Math.sin(frame * Math.PI / 2) * 25 : 0;
  const bounce = isMoving ? Math.abs(Math.sin(frame * Math.PI / 2)) * 2 : 0;
  
  // Pixel sizing helper
  const px = (v) => Math.round(v * (size / 100)) + 'px';
  
  // 3D Cube component - renders a blocky cube with visible top and side faces
  const Cube3D = ({ 
    x, y, width, height, depth, 
    topColor, frontColor, sideColor, 
    borderRadius = 0, 
    style = {},
    children 
  }) => (
    <div style={{
      position: 'absolute',
      left: px(x),
      bottom: px(y),
      width: px(width),
      height: px(height),
      ...style,
    }}>
      {/* Top face (visible bevel) */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: px(depth),
        backgroundColor: topColor,
        borderRadius: `${px(borderRadius)} ${px(borderRadius)} 0 0`,
        borderBottom: `1px solid ${adjustColor(topColor, -15)}`,
      }} />
      {/* Front face */}
      <div style={{
        position: 'absolute',
        top: px(depth),
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: frontColor,
        borderRadius: `0 0 ${px(borderRadius)} ${px(borderRadius)}`,
      }}>
        {children}
      </div>
      {/* Right side face (3D edge) */}
      <div style={{
        position: 'absolute',
        top: px(depth),
        right: 0,
        width: px(Math.max(3, depth * 0.5)),
        bottom: 0,
        backgroundColor: sideColor,
        borderRadius: `0 0 ${px(borderRadius)} 0`,
      }} />
    </div>
  );

  // Foot / shoe rendering based on selected shoe style
  const Foot = ({ w = 24, h = 14, brW = 4, brTop = 3 }) => {
    if (shoes === 'barefoot') {
      return (
        <div style={{
          width: px(w), height: px(h), marginTop: px(-2), marginLeft: px(-1),
          backgroundColor: skinFront,
          borderRight: `${px(Math.max(2, brW - 1))} solid ${skinSide}`,
          borderTop: `${px(Math.max(1, brTop - 1))} solid ${skinTop}`,
          borderRadius: `${px(2)} ${px(2)} ${px(7)} ${px(7)}`,
        }} />
      );
    }
    if (shoes === 'sandals') {
      return (
        <div style={{
          position: 'relative',
          width: px(w), height: px(h), marginTop: px(-2), marginLeft: px(-1),
          backgroundColor: skinFront,
          borderRadius: `${px(2)} ${px(2)} ${px(4)} ${px(4)}`,
        }}>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: px(4), backgroundColor: '#8B5E3C', borderRadius: `0 0 ${px(3)} ${px(3)}` }} />
          <div style={{ position: 'absolute', top: px(2), left: '18%', right: '18%', height: px(3), backgroundColor: '#8B5E3C', borderRadius: px(2) }} />
        </div>
      );
    }
    if (shoes === 'boots') {
      return (
        <div style={{
          width: px(w), height: px(h + 8), marginTop: px(-10), marginLeft: px(-1),
          backgroundColor: '#6B4423',
          borderRight: `${px(brW)} solid #4A2F16`,
          borderTop: `${px(brTop)} solid #8B5E3C`,
          borderRadius: px(3),
        }} />
      );
    }
    // trainers (default) - matches the original dark shoe look
    return (
      <div style={{
        width: px(w), height: px(h), marginTop: px(-2), marginLeft: px(-1),
        backgroundColor: '#2D3748',
        borderRight: `${px(brW)} solid #1A202C`,
        borderTop: `${px(brTop)} solid #4A5568`,
        borderRadius: px(2),
      }} />
    );
  };

  // Side view rendering
  if (isSide) {
    return (
      <div 
        style={{ 
          position: 'relative',
          width: size, 
          height: size * 1.6,
          transform: `translateY(${-bounce}px) ${isRight ? 'scaleX(-1)' : ''}`,
          filter: 'drop-shadow(0 6px 8px rgba(0,0,0,0.35))',
        }}
        data-testid="blocky-character-side"
      >
        {/* Ground shadow */}
        <div style={{
          position: 'absolute', bottom: px(0), left: '50%', transform: 'translateX(-50%)',
          width: px(50), height: px(10), borderRadius: '50%', 
          background: 'radial-gradient(ellipse, rgba(0,0,0,0.4) 0%, transparent 70%)',
        }} />
        
        {/* Back leg */}
        <div style={{
          position: 'absolute', bottom: px(8), left: px(32),
          width: px(20), height: px(38),
          transform: `rotate(${-legAngle}deg)`, transformOrigin: 'top center',
        }}>
          <div style={{
            width: '100%', height: px(28),
            backgroundColor: pantsFront,
            borderRight: `${px(4)} solid ${pantsSide}`,
            borderTop: `${px(3)} solid ${pantsTop}`,
          }} />
          <Foot w={22} h={12} brW={3} brTop={2} />
        </div>
        
        {/* Body */}
        <div style={{
          position: 'absolute', bottom: px(40), left: '50%', transform: 'translateX(-50%)',
          width: px(38), height: px(38),
          backgroundColor: shirtFront,
          borderRight: `${px(5)} solid ${shirtSide}`,
          borderTop: `${px(4)} solid ${shirtTop}`,
          borderRadius: px(2),
        }} />
        
        {/* Front leg */}
        <div style={{
          position: 'absolute', bottom: px(8), left: px(42),
          width: px(20), height: px(38),
          transform: `rotate(${legAngle}deg)`, transformOrigin: 'top center',
          zIndex: 2,
        }}>
          <div style={{
            width: '100%', height: px(28),
            backgroundColor: pantsFront,
            borderRight: `${px(4)} solid ${pantsSide}`,
            borderTop: `${px(3)} solid ${pantsTop}`,
          }} />
          <Foot w={22} h={12} brW={3} brTop={2} />
        </div>
        
        {/* Arms */}
        <div style={{
          position: 'absolute', bottom: px(48), left: px(20),
          width: px(16), height: px(36),
          backgroundColor: shirtFront,
          borderRight: `${px(3)} solid ${shirtSide}`,
          borderTop: `${px(3)} solid ${shirtTop}`,
          transform: `rotate(${-armAngle}deg)`, transformOrigin: 'top center',
          borderRadius: px(2),
          zIndex: 1,
        }}>
          {/* Hand */}
          <div style={{
            position: 'absolute', bottom: px(-4), left: '50%', transform: 'translateX(-50%)',
            width: px(14), height: px(12),
            backgroundColor: skinFront,
            borderRight: `${px(2)} solid ${skinSide}`,
            borderTop: `${px(2)} solid ${skinTop}`,
            borderRadius: px(2),
          }} />
        </div>
        
        {/* Neck */}
        <div style={{
          position: 'absolute', bottom: px(74), left: '50%', transform: 'translateX(-50%)',
          width: px(14), height: px(8),
          backgroundColor: skinFront,
          borderRight: `${px(2)} solid ${skinSide}`,
          zIndex: 3,
        }} />
        
        {/* Head - Blocky cube */}
        <div style={{
          position: 'absolute', bottom: px(78), left: '50%', transform: 'translateX(-50%)',
          width: px(48), height: px(48),
          backgroundColor: skinFront,
          borderRight: `${px(6)} solid ${skinSide}`,
          borderTop: `${px(5)} solid ${skinTop}`,
          borderRadius: px(3),
          zIndex: 4,
        }}>
          {/* Side profile face */}
          {renderSideProfileFace(px)}
        </div>
        
        {/* Hair */}
        {renderHairSide(hairStyle, hairFront, hairSide, hairTop, px)}
        
        {/* Accessory */}
        {renderAccessorySide(accessory, size, px)}
      </div>
    );
  }

  // Front/Back view rendering
  return (
    <div 
      style={{ 
        position: 'relative',
        width: size, 
        height: size * 1.6,
        transform: `translateY(${-bounce}px)`,
        filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.35))',
      }}
      data-testid="blocky-character"
    >
      {/* Name tag */}
      {showNameTag && name && (
        <div style={{
          position: 'absolute', top: px(-25), left: '50%', transform: 'translateX(-50%)',
          padding: `${px(3)} ${px(10)}`, backgroundColor: 'rgba(0,0,0,0.8)',
          borderRadius: px(8), color: 'white', fontSize: px(10), fontWeight: 'bold',
          whiteSpace: 'nowrap', zIndex: 100, textShadow: '0 1px 2px rgba(0,0,0,0.5)',
        }}>
          {name}
        </div>
      )}
      
      {/* Ground shadow */}
      <div style={{
        position: 'absolute', bottom: px(0), left: '50%', transform: 'translateX(-50%)',
        width: px(55), height: px(12), borderRadius: '50%', 
        background: 'radial-gradient(ellipse, rgba(0,0,0,0.45) 0%, transparent 70%)',
      }} />
      
      {/* Left Leg */}
      <div style={{
        position: 'absolute', bottom: px(6), left: px(25),
        width: px(22), height: px(42),
        transform: `rotate(${legAngle}deg)`, transformOrigin: 'top center',
      }}>
        {/* Leg block */}
        <div style={{
          width: '100%', height: px(30),
          backgroundColor: pantsFront,
          borderRight: `${px(4)} solid ${pantsSide}`,
          borderTop: `${px(3)} solid ${pantsTop}`,
          borderRadius: `${px(2)} ${px(2)} 0 0`,
        }} />
        {/* Foot / shoe */}
        <Foot />
      </div>
      
      {/* Right Leg */}
      <div style={{
        position: 'absolute', bottom: px(6), right: px(25),
        width: px(22), height: px(42),
        transform: `rotate(${-legAngle}deg)`, transformOrigin: 'top center',
      }}>
        {/* Leg block */}
        <div style={{
          width: '100%', height: px(30),
          backgroundColor: pantsFront,
          borderRight: `${px(4)} solid ${pantsSide}`,
          borderTop: `${px(3)} solid ${pantsTop}`,
          borderRadius: `${px(2)} ${px(2)} 0 0`,
        }} />
        {/* Foot / shoe */}
        <Foot />
      </div>
      
      {/* Body - Main torso block */}
      <div style={{
        position: 'absolute', bottom: px(42), left: '50%', transform: 'translateX(-50%)',
        width: px(52), height: px(42),
        backgroundColor: shirtFront,
        borderRight: `${px(6)} solid ${shirtSide}`,
        borderTop: `${px(5)} solid ${shirtTop}`,
        borderRadius: px(3),
      }}>
        {/* Shirt collar detail */}
        <div style={{
          position: 'absolute', top: px(4), left: '50%', transform: 'translateX(-50%)',
          width: px(14), height: px(10),
          backgroundColor: adjustColor(shirtColor, -20),
          borderRadius: `0 0 ${px(4)} ${px(4)}`,
        }} />
      </div>
      
      {/* Left Arm */}
      <div style={{
        position: 'absolute', bottom: px(46), left: px(8),
        width: px(18), height: px(38),
        backgroundColor: shirtFront,
        borderRight: `${px(4)} solid ${shirtSide}`,
        borderTop: `${px(3)} solid ${shirtTop}`,
        transform: `rotate(${-armAngle}deg)`, transformOrigin: 'top center',
        borderRadius: px(2),
      }}>
        {/* Hand */}
        <div style={{
          position: 'absolute', bottom: px(-4), left: '50%', transform: 'translateX(-50%)',
          width: px(16), height: px(14),
          backgroundColor: skinFront,
          borderRight: `${px(3)} solid ${skinSide}`,
          borderTop: `${px(2)} solid ${skinTop}`,
          borderRadius: px(2),
        }} />
      </div>
      
      {/* Right Arm */}
      <div style={{
        position: 'absolute', bottom: px(46), right: px(8),
        width: px(18), height: px(38),
        backgroundColor: shirtFront,
        borderRight: `${px(4)} solid ${shirtSide}`,
        borderTop: `${px(3)} solid ${shirtTop}`,
        transform: `rotate(${armAngle}deg)`, transformOrigin: 'top center',
        borderRadius: px(2),
      }}>
        {/* Hand */}
        <div style={{
          position: 'absolute', bottom: px(-4), left: '50%', transform: 'translateX(-50%)',
          width: px(16), height: px(14),
          backgroundColor: skinFront,
          borderRight: `${px(3)} solid ${skinSide}`,
          borderTop: `${px(2)} solid ${skinTop}`,
          borderRadius: px(2),
        }} />
      </div>
      
      {/* Neck */}
      <div style={{
        position: 'absolute', bottom: px(80), left: '50%', transform: 'translateX(-50%)',
        width: px(18), height: px(8),
        backgroundColor: skinFront,
        borderRight: `${px(3)} solid ${skinSide}`,
        zIndex: 3,
      }} />
      
      {/* Head - THE BLOCKY CUBE */}
      <div style={{
        position: 'absolute', bottom: px(84), left: '50%', transform: 'translateX(-50%)',
        width: px(52), height: px(52),
        backgroundColor: skinFront,
        borderRight: `${px(7)} solid ${skinSide}`,
        borderTop: `${px(6)} solid ${skinTop}`,
        borderRadius: px(4),
        zIndex: 4,
      }}>
        {/* Face features */}
        {!isBack && renderFace(eyeStyle, mouthStyle, skinSide, px)}
      </div>
      
      {/* Hair */}
      {!isBack && renderHairFront(hairStyle, hairFront, hairSide, hairTop, size, px)}
      {isBack && renderHairBack(hairStyle, hairFront, hairSide, hairTop, px)}
      
      {/* Accessories */}
      {!isBack && renderAccessory(accessory, size, px, skinTop)}
    </div>
  );
});

// Helper function to adjust color brightness
function adjustColor(hex, percent) {
  if (!hex || hex.length < 4) return '#888888';
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
  return `#${(1 << 24 | R << 16 | G << 8 | B).toString(16).slice(1)}`;
}

// Render side profile face
function renderSideProfileFace(px) {
  return (
    <>
      {/* Eye */}
      <div style={{
        position: 'absolute', top: px(14), left: px(8),
        width: px(10), height: px(10),
        backgroundColor: 'white',
        borderRadius: px(2),
        border: '1px solid #E5E5E5',
      }}>
        <div style={{
          position: 'absolute', top: '25%', left: '35%',
          width: '50%', height: '55%',
          backgroundColor: '#1a1a1a', borderRadius: '50%',
        }}>
          <div style={{
            position: 'absolute', top: '15%', left: '15%',
            width: '35%', height: '35%',
            backgroundColor: 'white', borderRadius: '50%',
          }} />
        </div>
      </div>
      {/* Nose bump */}
      <div style={{
        position: 'absolute', top: px(22), left: px(-3),
        width: px(8), height: px(8),
        backgroundColor: 'inherit',
        borderRadius: px(2),
      }} />
      {/* Mouth */}
      <div style={{
        position: 'absolute', bottom: px(10), left: px(6),
        width: px(12), height: px(3),
        backgroundColor: '#D97706',
        borderRadius: px(1),
      }} />
    </>
  );
}

// Render face features (front view)
function renderFace(eyeStyle, mouthStyle, skinSide, px) {
  // Roblox-style blocky eyes with large pupils
  const EyeBlock = ({ left }) => (
    <div style={{
      position: 'absolute', top: px(12), [left ? 'left' : 'right']: px(6),
      width: px(14), height: px(14),
      backgroundColor: 'white',
      borderRight: `${px(2)} solid #E5E5E5`,
      borderTop: `${px(2)} solid #F5F5F5`,
      borderRadius: px(3),
      overflow: 'hidden',
    }}>
      {/* Large black pupil - Roblox style */}
      <div style={{
        position: 'absolute', 
        top: '50%', 
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '65%', 
        height: '65%',
        backgroundColor: '#1a1a1a', 
        borderRadius: '50%',
      }}>
        {/* White highlight */}
        <div style={{
          position: 'absolute', top: '10%', left: '15%',
          width: '35%', height: '35%',
          backgroundColor: 'white', borderRadius: '50%',
        }} />
      </div>
    </div>
  );

  return (
    <>
      {/* Eyes based on style */}
      {eyeStyle === 'normal' && (
        <>
          <EyeBlock left />
          <EyeBlock />
        </>
      )}
      {eyeStyle === 'happy' && (
        <>
          <div style={{
            position: 'absolute', top: px(16), left: px(6),
            width: px(14), height: px(7),
            borderBottom: `${px(4)} solid #1a1a1a`,
            borderRadius: '0 0 50% 50%',
          }} />
          <div style={{
            position: 'absolute', top: px(16), right: px(6),
            width: px(14), height: px(7),
            borderBottom: `${px(4)} solid #1a1a1a`,
            borderRadius: '0 0 50% 50%',
          }} />
        </>
      )}
      {eyeStyle === 'cool' && (
        <>
          <div style={{
            position: 'absolute', top: px(12), left: px(4),
            width: px(18), height: px(12),
            backgroundColor: '#1a1a1a',
            borderRight: `${px(2)} solid #333`,
            borderTop: `${px(2)} solid #444`,
            borderRadius: px(3),
          }} />
          <div style={{
            position: 'absolute', top: px(12), right: px(4),
            width: px(18), height: px(12),
            backgroundColor: '#1a1a1a',
            borderRight: `${px(2)} solid #333`,
            borderTop: `${px(2)} solid #444`,
            borderRadius: px(3),
          }} />
          {/* Sunglasses bridge */}
          <div style={{
            position: 'absolute', top: px(15), left: '50%', transform: 'translateX(-50%)',
            width: px(6), height: px(4),
            backgroundColor: '#1a1a1a',
          }} />
        </>
      )}
      {eyeStyle === 'wink' && (
        <>
          <EyeBlock left />
          <div style={{
            position: 'absolute', top: px(18), right: px(6),
            width: px(14), height: px(4),
            backgroundColor: '#1a1a1a', borderRadius: px(2),
          }} />
        </>
      )}
      {eyeStyle === 'surprised' && (
        <>
          <div style={{
            position: 'absolute', top: px(10), left: px(5),
            width: px(15), height: px(15),
            backgroundColor: 'white',
            borderRight: `${px(2)} solid #E5E5E5`,
            borderRadius: '50%',
          }}>
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '50%', height: '50%',
              backgroundColor: '#1a1a1a', borderRadius: '50%',
            }} />
          </div>
          <div style={{
            position: 'absolute', top: px(10), right: px(5),
            width: px(15), height: px(15),
            backgroundColor: 'white',
            borderRight: `${px(2)} solid #E5E5E5`,
            borderRadius: '50%',
          }}>
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '50%', height: '50%',
              backgroundColor: '#1a1a1a', borderRadius: '50%',
            }} />
          </div>
        </>
      )}
      {eyeStyle === 'sleepy' && (
        <>
          <div style={{
            position: 'absolute', top: px(18), left: px(6),
            width: px(14), height: px(6),
            backgroundColor: '#1a1a1a',
            borderRadius: `0 0 ${px(8)} ${px(8)}`,
          }} />
          <div style={{
            position: 'absolute', top: px(18), right: px(6),
            width: px(14), height: px(6),
            backgroundColor: '#1a1a1a',
            borderRadius: `0 0 ${px(8)} ${px(8)}`,
          }} />
        </>
      )}
      {eyeStyle === 'excited' && (
        <>
          <div style={{
            position: 'absolute', top: px(8), left: px(4),
            width: px(16), height: px(18),
            backgroundColor: 'white',
            borderRight: `${px(2)} solid #E5E5E5`,
            borderRadius: px(3),
          }}>
            <div style={{
              position: 'absolute', top: px(6), left: '50%',
              transform: 'translateX(-50%)',
              width: px(10), height: px(10),
              backgroundColor: '#1a1a1a', borderRadius: '50%',
            }}>
              <div style={{
                position: 'absolute', top: px(2), left: px(2),
                width: px(3), height: px(3),
                backgroundColor: 'white', borderRadius: '50%',
              }} />
            </div>
          </div>
          <div style={{
            position: 'absolute', top: px(8), right: px(4),
            width: px(16), height: px(18),
            backgroundColor: 'white',
            borderRight: `${px(2)} solid #E5E5E5`,
            borderRadius: px(3),
          }}>
            <div style={{
              position: 'absolute', top: px(6), left: '50%',
              transform: 'translateX(-50%)',
              width: px(10), height: px(10),
              backgroundColor: '#1a1a1a', borderRadius: '50%',
            }}>
              <div style={{
                position: 'absolute', top: px(2), left: px(2),
                width: px(3), height: px(3),
                backgroundColor: 'white', borderRadius: '50%',
              }} />
            </div>
          </div>
        </>
      )}
      {eyeStyle === 'angry' && (
        <>
          {/* Left angry eye with brow */}
          <div style={{
            position: 'absolute', top: px(8), left: px(2),
            width: px(18), height: px(6),
            backgroundColor: skinTop,
            transform: 'rotate(15deg)',
            borderRadius: px(2),
          }} />
          <div style={{
            position: 'absolute', top: px(14), left: px(5),
            width: px(14), height: px(12),
            backgroundColor: 'white',
            borderRight: `${px(2)} solid #E5E5E5`,
            borderRadius: px(3),
          }}>
            <div style={{
              position: 'absolute', top: px(3), left: '50%',
              transform: 'translateX(-50%)',
              width: px(8), height: px(6),
              backgroundColor: '#1a1a1a', borderRadius: px(2),
            }} />
          </div>
          {/* Right angry eye with brow */}
          <div style={{
            position: 'absolute', top: px(8), right: px(2),
            width: px(18), height: px(6),
            backgroundColor: skinTop,
            transform: 'rotate(-15deg)',
            borderRadius: px(2),
          }} />
          <div style={{
            position: 'absolute', top: px(14), right: px(5),
            width: px(14), height: px(12),
            backgroundColor: 'white',
            borderRight: `${px(2)} solid #E5E5E5`,
            borderRadius: px(3),
          }}>
            <div style={{
              position: 'absolute', top: px(3), left: '50%',
              transform: 'translateX(-50%)',
              width: px(8), height: px(6),
              backgroundColor: '#1a1a1a', borderRadius: px(2),
            }} />
          </div>
        </>
      )}
      
      {/* Mouth based on style */}
      {mouthStyle === 'smile' && (
        <div style={{
          position: 'absolute', bottom: px(10), left: '50%', transform: 'translateX(-50%)',
          width: px(18), height: px(8),
          backgroundColor: '#DC2626',
          borderRight: `${px(2)} solid #B91C1C`,
          borderTop: `${px(2)} solid #EF4444`,
          borderRadius: `0 0 ${px(10)} ${px(10)}`,
        }} />
      )}
      {mouthStyle === 'open' && (
        <div style={{
          position: 'absolute', bottom: px(8), left: '50%', transform: 'translateX(-50%)',
          width: px(14), height: px(12),
          backgroundColor: '#1a1a1a',
          borderRadius: '50%',
        }} />
      )}
      {mouthStyle === 'flat' && (
        <div style={{
          position: 'absolute', bottom: px(12), left: '50%', transform: 'translateX(-50%)',
          width: px(16), height: px(4),
          backgroundColor: '#DC2626',
          borderRight: `${px(2)} solid #B91C1C`,
          borderRadius: px(2),
        }} />
      )}
      {mouthStyle === 'neutral' && (
        <div style={{
          position: 'absolute', bottom: px(12), left: '50%', transform: 'translateX(-50%)',
          width: px(16), height: px(4),
          backgroundColor: '#DC2626',
          borderRight: `${px(2)} solid #B91C1C`,
          borderRadius: px(2),
        }} />
      )}
      {mouthStyle === 'grin' && (
        <div style={{
          position: 'absolute', bottom: px(8), left: '50%', transform: 'translateX(-50%)',
          width: px(22), height: px(10),
          backgroundColor: '#DC2626',
          borderRight: `${px(2)} solid #B91C1C`,
          borderTop: `${px(2)} solid #EF4444`,
          borderRadius: `0 0 ${px(12)} ${px(12)}`,
        }}>
          {/* Teeth */}
          <div style={{
            position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
            width: px(16), height: px(4),
            backgroundColor: 'white',
            borderRadius: `0 0 ${px(2)} ${px(2)}`,
          }} />
        </div>
      )}
      {mouthStyle === 'tongue' && (
        <div style={{
          position: 'absolute', bottom: px(6), left: '50%', transform: 'translateX(-50%)',
        }}>
          <div style={{
            width: px(18), height: px(8),
            backgroundColor: '#DC2626',
            borderRight: `${px(2)} solid #B91C1C`,
            borderRadius: `0 0 ${px(10)} ${px(10)}`,
          }} />
          {/* Tongue sticking out */}
          <div style={{
            position: 'absolute', bottom: px(-8), left: '50%', transform: 'translateX(-50%)',
            width: px(12), height: px(12),
            backgroundColor: '#FF6B6B',
            borderRight: `${px(2)} solid #EF4444`,
            borderRadius: `0 0 ${px(8)} ${px(8)}`,
          }} />
        </div>
      )}
      {mouthStyle === 'surprised' && (
        <div style={{
          position: 'absolute', bottom: px(8), left: '50%', transform: 'translateX(-50%)',
          width: px(12), height: px(14),
          backgroundColor: '#1a1a1a',
          borderRadius: '50%',
        }} />
      )}
      {mouthStyle === 'teeth' && (
        <div style={{
          position: 'absolute', bottom: px(8), left: '50%', transform: 'translateX(-50%)',
          width: px(22), height: px(12),
          backgroundColor: '#DC2626',
          borderRight: `${px(2)} solid #B91C1C`,
          borderRadius: px(3),
        }}>
          {/* Top teeth */}
          <div style={{
            position: 'absolute', top: px(2), left: '50%', transform: 'translateX(-50%)',
            width: px(18), height: px(5),
            backgroundColor: 'white',
            borderRadius: px(2),
          }}>
            {[0, 6, 12].map((offset, i) => (
              <div key={i} style={{
                position: 'absolute', top: 0, left: px(1 + offset),
                width: px(1), height: px(5),
                backgroundColor: '#E5E5E5',
              }} />
            ))}
          </div>
        </div>
      )}
      {mouthStyle === 'kiss' && (
        <div style={{
          position: 'absolute', bottom: px(10), left: '50%', transform: 'translateX(-50%)',
          width: px(10), height: px(10),
          backgroundColor: '#EC4899',
          borderRadius: '50%',
        }}>
          <div style={{
            position: 'absolute', top: px(2), left: '50%', transform: 'translateX(-50%)',
            width: px(6), height: px(4),
            backgroundColor: '#F472B6',
            borderRadius: '50%',
          }} />
        </div>
      )}
      
      {/* Cheek blush */}
      <div style={{
        position: 'absolute', top: px(26), left: px(5),
        width: px(8), height: px(4),
        backgroundColor: '#FCA5A5', borderRadius: '50%', opacity: 0.6,
      }} />
      <div style={{
        position: 'absolute', top: px(26), right: px(5),
        width: px(8), height: px(4),
        backgroundColor: '#FCA5A5', borderRadius: '50%', opacity: 0.6,
      }} />
    </>
  );
}

// Render hair for front view with blocky style
function renderHairFront(hairStyle, hairFront, hairSide, hairTop, size, px) {
  const baseStyle = {
    position: 'absolute',
    bottom: px(128),
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 5,
  };
  
  switch (hairStyle) {
    case 'short':
      return (
        <div style={{
          ...baseStyle,
          width: px(54), height: px(18),
          backgroundColor: hairFront,
          borderRight: `${px(5)} solid ${hairSide}`,
          borderTop: `${px(4)} solid ${hairTop}`,
          borderRadius: `${px(4)} ${px(4)} 0 0`,
        }}>
          {/* Hair texture blocks */}
          <div style={{
            position: 'absolute', top: px(2), left: px(5),
            width: px(8), height: px(10),
            backgroundColor: hairSide, borderRadius: px(2),
          }} />
          <div style={{
            position: 'absolute', top: px(4), left: px(18),
            width: px(6), height: px(8),
            backgroundColor: hairSide, borderRadius: px(2),
          }} />
          <div style={{
            position: 'absolute', top: px(2), right: px(8),
            width: px(7), height: px(10),
            backgroundColor: hairSide, borderRadius: px(2),
          }} />
        </div>
      );
    case 'spiky':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(54), height: px(20),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(4)} ${px(4)} 0 0`,
          }} />
          {/* Spikes */}
          {[0, 12, 24, 36].map((offset, i) => (
            <div key={i} style={{
              position: 'absolute', top: px(-12 - (i % 2) * 4),
              left: px(6 + offset),
              width: px(10), height: px(16),
              backgroundColor: i % 2 === 0 ? hairFront : hairSide,
              borderRadius: `${px(3)} ${px(3)} 0 0`,
              transform: `rotate(${(i - 1.5) * 8}deg)`,
            }} />
          ))}
        </div>
      );
    case 'curly':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(58), height: px(24),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(6)} ${px(6)} ${px(3)} ${px(3)}`,
          }}>
            {/* Curly bumps */}
            {[0, 14, 28, 42].map((offset, i) => (
              <div key={i} style={{
                position: 'absolute', top: px(-6),
                left: px(3 + offset),
                width: px(12), height: px(12),
                backgroundColor: i % 2 === 0 ? hairTop : hairFront,
                borderRadius: '50%',
              }} />
            ))}
          </div>
        </div>
      );
    case 'long':
      return (
        <div style={baseStyle}>
          {/* Main hair block */}
          <div style={{
            width: px(56), height: px(22),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(4)} ${px(4)} 0 0`,
          }} />
          {/* Long side pieces */}
          <div style={{
            position: 'absolute', top: px(18), left: px(-4),
            width: px(14), height: px(50),
            backgroundColor: hairFront,
            borderRight: `${px(3)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(4)} ${px(4)}`,
          }} />
          <div style={{
            position: 'absolute', top: px(18), right: px(-4),
            width: px(14), height: px(50),
            backgroundColor: hairFront,
            borderRight: `${px(3)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(4)} ${px(4)}`,
          }} />
        </div>
      );
    case 'ponytail':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(54), height: px(18),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(4)} ${px(4)} 0 0`,
          }} />
          {/* Hair tie visible at top */}
          <div style={{
            position: 'absolute', top: px(2), left: '50%', transform: 'translateX(-50%)',
            width: px(12), height: px(6),
            backgroundColor: '#EF4444',
            borderRadius: px(2),
            zIndex: 1,
          }} />
        </div>
      );
    case 'bob':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(58), height: px(22),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(6)} ${px(6)} 0 0`,
          }} />
          {/* Bob sides curving inward */}
          <div style={{
            position: 'absolute', top: px(16), left: px(-2),
            width: px(14), height: px(30),
            backgroundColor: hairFront,
            borderRight: `${px(3)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(8)} ${px(8)}`,
          }} />
          <div style={{
            position: 'absolute', top: px(16), right: px(-2),
            width: px(14), height: px(30),
            backgroundColor: hairFront,
            borderRight: `${px(3)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(8)} ${px(8)}`,
          }} />
        </div>
      );
    case 'buzz':
      return (
        <div style={{
          ...baseStyle,
          width: px(54), height: px(12),
          backgroundColor: hairFront,
          borderRight: `${px(5)} solid ${hairSide}`,
          borderTop: `${px(3)} solid ${hairTop}`,
          borderRadius: `${px(3)} ${px(3)} 0 0`,
          opacity: 0.85,
        }} />
      );
    case 'pigtails':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(54), height: px(18),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(4)} ${px(4)} 0 0`,
          }} />
          {/* Left pigtail */}
          <div style={{
            position: 'absolute', top: px(10), left: px(-10),
            width: px(14), height: px(35),
            backgroundColor: hairFront,
            borderRight: `${px(3)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(6)} ${px(6)}`,
          }} />
          {/* Right pigtail */}
          <div style={{
            position: 'absolute', top: px(10), right: px(-10),
            width: px(14), height: px(35),
            backgroundColor: hairFront,
            borderRight: `${px(3)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(6)} ${px(6)}`,
          }} />
          {/* Hair ties */}
          <div style={{
            position: 'absolute', top: px(12), left: px(-8),
            width: px(10), height: px(5),
            backgroundColor: '#EC4899', borderRadius: px(2),
          }} />
          <div style={{
            position: 'absolute', top: px(12), right: px(-8),
            width: px(10), height: px(5),
            backgroundColor: '#EC4899', borderRadius: px(2),
          }} />
        </div>
      );
    case 'braids':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(54), height: px(18),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(4)} ${px(4)} 0 0`,
          }} />
          {/* Left braid */}
          <div style={{
            position: 'absolute', top: px(14), left: px(-6),
            width: px(10), height: px(50),
            backgroundColor: hairFront,
            borderRight: `${px(2)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(4)} ${px(4)}`,
          }}>
            {[0, 10, 20, 30, 40].map((y, i) => (
              <div key={i} style={{
                position: 'absolute', top: px(y), left: i % 2 === 0 ? 0 : px(3),
                width: px(7), height: px(8),
                backgroundColor: i % 2 === 0 ? hairSide : hairFront,
                borderRadius: px(2),
              }} />
            ))}
          </div>
          {/* Right braid */}
          <div style={{
            position: 'absolute', top: px(14), right: px(-6),
            width: px(10), height: px(50),
            backgroundColor: hairFront,
            borderRight: `${px(2)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(4)} ${px(4)}`,
          }}>
            {[0, 10, 20, 30, 40].map((y, i) => (
              <div key={i} style={{
                position: 'absolute', top: px(y), left: i % 2 === 0 ? px(3) : 0,
                width: px(7), height: px(8),
                backgroundColor: i % 2 === 0 ? hairSide : hairFront,
                borderRadius: px(2),
              }} />
            ))}
          </div>
        </div>
      );
    case 'afro':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(70), height: px(40),
            backgroundColor: hairFront,
            borderRight: `${px(6)} solid ${hairSide}`,
            borderTop: `${px(5)} solid ${hairTop}`,
            borderRadius: `${px(20)} ${px(20)} ${px(6)} ${px(6)}`,
          }}>
            {/* Texture bumps */}
            {[0, 16, 32, 48].map((x, i) => (
              <div key={i} style={{
                position: 'absolute', top: px(-4),
                left: px(5 + x),
                width: px(12), height: px(12),
                backgroundColor: i % 2 === 0 ? hairTop : hairFront,
                borderRadius: '50%',
              }} />
            ))}
          </div>
          {/* Side puffs */}
          <div style={{
            position: 'absolute', top: px(12), left: px(-8),
            width: px(16), height: px(30),
            backgroundColor: hairFront,
            borderRadius: `${px(8)} 0 ${px(8)} ${px(8)}`,
          }} />
          <div style={{
            position: 'absolute', top: px(12), right: px(-8),
            width: px(16), height: px(30),
            backgroundColor: hairFront,
            borderRadius: `0 ${px(8)} ${px(8)} ${px(8)}`,
          }} />
        </div>
      );
    case 'mohawk':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(54), height: px(14),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(3)} solid ${hairTop}`,
            borderRadius: `${px(3)} ${px(3)} 0 0`,
          }} />
          {/* Mohawk ridge */}
          <div style={{
            position: 'absolute', top: px(-20), left: '50%', transform: 'translateX(-50%)',
            width: px(14), height: px(28),
            backgroundColor: hairFront,
            borderRight: `${px(3)} solid ${hairSide}`,
            borderTop: `${px(3)} solid ${hairTop}`,
            borderRadius: `${px(4)} ${px(4)} 0 0`,
          }} />
        </div>
      );
    case 'bun':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(54), height: px(18),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(4)} ${px(4)} 0 0`,
          }} />
          {/* Bun on top */}
          <div style={{
            position: 'absolute', top: px(-16), left: '50%', transform: 'translateX(-50%)',
            width: px(22), height: px(22),
            backgroundColor: hairFront,
            borderRight: `${px(4)} solid ${hairSide}`,
            borderTop: `${px(3)} solid ${hairTop}`,
            borderRadius: '50%',
          }} />
        </div>
      );
    case 'wavy':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(58), height: px(24),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(8)} ${px(8)} 0 0`,
          }}>
            {/* Wavy texture */}
            {[0, 18, 36].map((offset, i) => (
              <div key={i} style={{
                position: 'absolute', top: px(-4),
                left: px(6 + offset),
                width: px(14), height: px(10),
                backgroundColor: i % 2 === 0 ? hairTop : hairFront,
                borderRadius: '50%',
              }} />
            ))}
          </div>
          {/* Wavy sides */}
          <div style={{
            position: 'absolute', top: px(18), left: px(-4),
            width: px(14), height: px(35),
            backgroundColor: hairFront,
            borderRight: `${px(3)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(8)} ${px(8)}`,
          }} />
          <div style={{
            position: 'absolute', top: px(18), right: px(-4),
            width: px(14), height: px(35),
            backgroundColor: hairFront,
            borderRight: `${px(3)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(8)} ${px(8)}`,
          }} />
        </div>
      );
    case 'undercut':
      return (
        <div style={baseStyle}>
          {/* Top hair */}
          <div style={{
            width: px(54), height: px(20),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(4)} ${px(4)} 0 0`,
          }} />
          {/* Shaved sides (subtle) */}
          <div style={{
            position: 'absolute', top: px(14), left: px(-2),
            width: px(8), height: px(12),
            backgroundColor: hairFront,
            opacity: 0.4,
            borderRadius: px(2),
          }} />
          <div style={{
            position: 'absolute', top: px(14), right: px(-2),
            width: px(8), height: px(12),
            backgroundColor: hairFront,
            opacity: 0.4,
            borderRadius: px(2),
          }} />
        </div>
      );
    case 'dreadlocks':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(58), height: px(18),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(4)} ${px(4)} 0 0`,
          }} />
          {/* Dreadlock strands - left ones drape over the face */}
          {[-8, 2, 12, 46, 54].map((offset, i) => (
            <div key={i} style={{
              position: 'absolute', top: px(10), left: px(offset),
              width: px(8), height: px(42 + (i % 2) * 8),
              backgroundColor: i % 2 === 0 ? hairFront : hairSide,
              borderRadius: `${px(2)} ${px(2)} ${px(4)} ${px(4)}`,
              zIndex: 6,
            }} />
          ))}
        </div>
      );
    case 'sidepart':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(54), height: px(18),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(4)} ${px(4)} 0 0`,
          }}>
            {/* Part line */}
            <div style={{
              position: 'absolute', top: px(2), left: px(14),
              width: px(2), height: px(12),
              backgroundColor: 'rgba(0,0,0,0.15)',
            }} />
          </div>
        </div>
      );
    case 'mullet':
      return (
        <div style={baseStyle}>
          <div style={{
            width: px(54), height: px(18),
            backgroundColor: hairFront,
            borderRight: `${px(5)} solid ${hairSide}`,
            borderTop: `${px(4)} solid ${hairTop}`,
            borderRadius: `${px(4)} ${px(4)} 0 0`,
          }} />
          {/* Business in front - short fringe */}
          <div style={{
            position: 'absolute', top: px(-2), left: px(8),
            width: px(38), height: px(8),
            backgroundColor: hairFront,
            borderRadius: `${px(2)} ${px(2)} 0 0`,
          }} />
          {/* Party in back - long back */}
          <div style={{
            position: 'absolute', top: px(14), left: '50%', transform: 'translateX(-50%)',
            width: px(44), height: px(50),
            backgroundColor: hairFront,
            borderRight: `${px(4)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(6)} ${px(6)}`,
          }} />
        </div>
      );
    case 'bald':
    default:
      return null;
  }
}

// Render hair for back view
function renderHairBack(hairStyle, hairFront, hairSide, hairTop, px) {
  const baseStyle = {
    position: 'absolute',
    bottom: px(128),
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 5,
  };
  
  if (hairStyle === 'bald') return null;
  
  const baseHairBack = (
    <div style={{
      ...baseStyle,
      width: px(54), height: px(20),
      backgroundColor: hairFront,
      borderRight: `${px(5)} solid ${hairSide}`,
      borderTop: `${px(4)} solid ${hairTop}`,
      borderRadius: `${px(4)} ${px(4)} 0 0`,
    }}>
      {hairStyle === 'long' && (
        <div style={{
          position: 'absolute', top: px(16), left: '50%', transform: 'translateX(-50%)',
          width: px(48), height: px(45),
          backgroundColor: hairFront,
          borderRight: `${px(5)} solid ${hairSide}`,
          borderRadius: `0 0 ${px(4)} ${px(4)}`,
        }} />
      )}
      {hairStyle === 'ponytail' && (
        <>
          <div style={{
            position: 'absolute', top: px(12), left: '50%', transform: 'translateX(-50%)',
            width: px(16), height: px(50),
            backgroundColor: hairFront,
            borderRight: `${px(3)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(6)} ${px(6)}`,
          }} />
          <div style={{
            position: 'absolute', top: px(14), left: '50%', transform: 'translateX(-50%)',
            width: px(12), height: px(6),
            backgroundColor: '#EF4444',
            borderRadius: px(2),
            zIndex: 1,
          }} />
        </>
      )}
      {hairStyle === 'braids' && (
        <>
          <div style={{
            position: 'absolute', top: px(14), left: px(4),
            width: px(10), height: px(50),
            backgroundColor: hairFront,
            borderRight: `${px(2)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(4)} ${px(4)}`,
          }} />
          <div style={{
            position: 'absolute', top: px(14), right: px(4),
            width: px(10), height: px(50),
            backgroundColor: hairFront,
            borderRight: `${px(2)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(4)} ${px(4)}`,
          }} />
        </>
      )}
      {hairStyle === 'pigtails' && (
        <>
          <div style={{
            position: 'absolute', top: px(10), left: px(-10),
            width: px(14), height: px(35),
            backgroundColor: hairFront,
            borderRight: `${px(3)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(6)} ${px(6)}`,
          }} />
          <div style={{
            position: 'absolute', top: px(10), right: px(-10),
            width: px(14), height: px(35),
            backgroundColor: hairFront,
            borderRight: `${px(3)} solid ${hairSide}`,
            borderRadius: `0 0 ${px(6)} ${px(6)}`,
          }} />
        </>
      )}
      {hairStyle === 'bob' && (
        <div style={{
          position: 'absolute', top: px(16), left: '50%', transform: 'translateX(-50%)',
          width: px(50), height: px(28),
          backgroundColor: hairFront,
          borderRight: `${px(5)} solid ${hairSide}`,
          borderRadius: `0 0 ${px(8)} ${px(8)}`,
        }} />
      )}
    </div>
  );

  if (hairStyle === 'afro') {
    return (
      <div style={{
        ...baseStyle,
        width: px(70), height: px(40),
        backgroundColor: hairFront,
        borderRight: `${px(6)} solid ${hairSide}`,
        borderTop: `${px(5)} solid ${hairTop}`,
        borderRadius: `${px(20)} ${px(20)} ${px(6)} ${px(6)}`,
      }}>
        <div style={{
          position: 'absolute', top: px(12), left: px(-8),
          width: px(16), height: px(30),
          backgroundColor: hairFront, borderRadius: px(8),
        }} />
        <div style={{
          position: 'absolute', top: px(12), right: px(-8),
          width: px(16), height: px(30),
          backgroundColor: hairFront, borderRadius: px(8),
        }} />
      </div>
    );
  }

  if (hairStyle === 'mohawk') {
    return (
      <div style={{
        ...baseStyle,
        width: px(54), height: px(14),
        backgroundColor: hairFront,
        borderRight: `${px(5)} solid ${hairSide}`,
        borderTop: `${px(3)} solid ${hairTop}`,
        borderRadius: `${px(3)} ${px(3)} 0 0`,
      }}>
        <div style={{
          position: 'absolute', top: px(-20), left: '50%', transform: 'translateX(-50%)',
          width: px(14), height: px(28),
          backgroundColor: hairFront,
          borderRight: `${px(3)} solid ${hairSide}`,
          borderRadius: `${px(4)} ${px(4)} 0 0`,
        }} />
      </div>
    );
  }

  return baseHairBack;
}

// Render hair for side view
function renderHairSide(hairStyle, hairFront, hairSide, hairTop, px) {
  if (hairStyle === 'bald') return null;
  
  return (
    <div style={{
      position: 'absolute', bottom: px(118), left: '50%', transform: 'translateX(-50%)',
      width: px(50), height: px(20),
      backgroundColor: hairFront,
      borderRight: `${px(5)} solid ${hairSide}`,
      borderTop: `${px(4)} solid ${hairTop}`,
      borderRadius: `${px(4)} ${px(4)} 0 0`,
      zIndex: 5,
    }}>
      {hairStyle === 'long' && (
        <div style={{
          position: 'absolute', top: px(16), right: px(-8),
          width: px(14), height: px(45),
          backgroundColor: hairFront,
          borderRight: `${px(3)} solid ${hairSide}`,
          borderRadius: `0 0 ${px(4)} ${px(4)}`,
        }} />
      )}
      {hairStyle === 'spiky' && (
        <div style={{
          position: 'absolute', top: px(-10), left: px(15),
          width: px(12), height: px(14),
          backgroundColor: hairFront,
          borderRadius: `${px(3)} ${px(3)} 0 0`,
        }} />
      )}
    </div>
  );
}

// Render accessories (front view)
function renderAccessory(accessory, size, px, skinTop) {
  switch (accessory) {
    case 'glasses':
      return (
        <div style={{
          position: 'absolute', bottom: px(116), left: '48%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          {/* Left lens */}
          <div style={{
            position: 'absolute', left: px(-20), top: 0,
            width: px(16), height: px(14),
            backgroundColor: 'rgba(100, 149, 237, 0.3)',
            border: `${px(2)} solid #1a1a1a`,
            borderRadius: px(3),
          }} />
          {/* Right lens */}
          <div style={{
            position: 'absolute', right: px(-20), top: 0,
            width: px(16), height: px(14),
            backgroundColor: 'rgba(100, 149, 237, 0.3)',
            border: `${px(2)} solid #1a1a1a`,
            borderRadius: px(3),
          }} />
          {/* Bridge */}
          <div style={{
            position: 'absolute', top: px(4), left: '50%', transform: 'translateX(-50%)',
            width: px(10), height: px(3),
            backgroundColor: '#1a1a1a',
          }} />
        </div>
      );
    case 'cap':
      return (
        <div style={{
          position: 'absolute', bottom: px(130), left: '50%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          {/* Cap crown */}
          <div style={{
            width: px(56), height: px(20),
            backgroundColor: '#DC2626',
            borderRight: `${px(5)} solid #B91C1C`,
            borderTop: `${px(4)} solid #EF4444`,
            borderRadius: `${px(6)} ${px(6)} 0 0`,
          }} />
          {/* Cap brim */}
          <div style={{
            position: 'absolute', bottom: px(-6), left: px(-4),
            width: px(32), height: px(10),
            backgroundColor: '#DC2626',
            borderRight: `${px(3)} solid #B91C1C`,
            borderTop: `${px(2)} solid #EF4444`,
            borderRadius: `${px(3)} ${px(3)} ${px(6)} ${px(6)}`,
          }} />
        </div>
      );
    case 'bow':
      return (
        <div style={{
          position: 'absolute', bottom: px(132), left: px(58),
          zIndex: 10,
        }}>
          <div style={{
            width: px(18), height: px(14),
            backgroundColor: '#EC4899',
            borderRight: `${px(3)} solid #BE185D`,
            borderTop: `${px(2)} solid #F472B6`,
            borderRadius: px(3),
          }} />
          <div style={{
            position: 'absolute', top: px(3), left: px(6),
            width: px(6), height: px(8),
            backgroundColor: '#BE185D',
            borderRadius: px(2),
          }} />
        </div>
      );
    case 'hat':
      return (
        <div style={{
          position: 'absolute', bottom: px(132), left: '50%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          {/* Hat brim */}
          <div style={{
            width: px(70), height: px(10),
            backgroundColor: '#8B4513',
            borderRight: `${px(4)} solid #654321`,
            borderTop: `${px(2)} solid #A0522D`,
            borderRadius: px(3),
          }} />
          {/* Hat crown */}
          <div style={{
            position: 'absolute', bottom: px(8), left: '50%', transform: 'translateX(-50%)',
            width: px(40), height: px(24),
            backgroundColor: '#8B4513',
            borderRight: `${px(4)} solid #654321`,
            borderTop: `${px(3)} solid #A0522D`,
            borderRadius: `${px(6)} ${px(6)} 0 0`,
          }}>
            {/* Hat band */}
            <div style={{
              position: 'absolute', bottom: px(4), left: 0, right: 0,
              height: px(4),
              backgroundColor: '#654321',
            }} />
          </div>
        </div>
      );
    case 'crown':
      return (
        <div style={{
          position: 'absolute', bottom: px(134), left: '50%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          {/* Crown base */}
          <div style={{
            width: px(48), height: px(14),
            backgroundColor: '#F59E0B',
            borderRight: `${px(4)} solid #D97706`,
            borderTop: `${px(3)} solid #FBBF24`,
            borderRadius: px(2),
          }} />
          {/* Crown points */}
          {[0, 16, 32].map((offset, i) => (
            <div key={i} style={{
              position: 'absolute', top: px(-10), left: px(5 + offset),
              width: px(10), height: px(12),
              backgroundColor: '#F59E0B',
              borderRight: `${px(2)} solid #D97706`,
              borderTop: `${px(2)} solid #FBBF24`,
              borderRadius: `${px(2)} ${px(2)} 0 0`,
            }}>
              {/* Gem */}
              <div style={{
                position: 'absolute', top: px(2), left: '50%', transform: 'translateX(-50%)',
                width: px(5), height: px(5),
                backgroundColor: i === 1 ? '#EF4444' : '#3B82F6',
                borderRadius: '50%',
              }} />
            </div>
          ))}
        </div>
      );
    case 'headphones':
      return (
        <div style={{
          position: 'absolute', bottom: px(118), left: '50%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          {/* Headband */}
          <div style={{
            position: 'absolute', top: px(-16), left: '50%', transform: 'translateX(-50%)',
            width: px(60), height: px(8),
            backgroundColor: '#1F2937',
            borderRadius: px(4),
            borderTop: `${px(2)} solid #374151`,
          }} />
          {/* Left ear cup - moved further left */}
          <div style={{
            position: 'absolute', left: px(-42), top: px(-8),
            width: px(16), height: px(20),
            backgroundColor: '#1F2937',
            borderRight: `${px(3)} solid #111827`,
            borderTop: `${px(2)} solid #374151`,
            borderRadius: px(4),
          }} />
          {/* Right ear cup - moved further right */}
          <div style={{
            position: 'absolute', right: px(-42), top: px(-8),
            width: px(16), height: px(20),
            backgroundColor: '#1F2937',
            borderRight: `${px(3)} solid #111827`,
            borderTop: `${px(2)} solid #374151`,
            borderRadius: px(4),
          }} />
        </div>
      );
    case 'sunglasses':
      return (
        <div style={{
          position: 'absolute', bottom: px(116), left: '48%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          {/* Left lens */}
          <div style={{
            position: 'absolute', left: px(-22), top: 0,
            width: px(18), height: px(16),
            backgroundColor: 'rgba(20, 20, 20, 0.75)',
            border: `${px(2)} solid #1a1a1a`,
            borderRadius: px(4),
          }} />
          {/* Right lens */}
          <div style={{
            position: 'absolute', right: px(-22), top: 0,
            width: px(18), height: px(16),
            backgroundColor: 'rgba(20, 20, 20, 0.75)',
            border: `${px(2)} solid #1a1a1a`,
            borderRadius: px(4),
          }} />
          {/* Bridge */}
          <div style={{
            position: 'absolute', top: px(4), left: '50%', transform: 'translateX(-50%)',
            width: px(10), height: px(3),
            backgroundColor: '#1a1a1a',
          }} />
        </div>
      );
    case 'headband':
      return (
        <div style={{
          position: 'absolute', bottom: px(126), left: '50%', transform: 'translateX(-50%)',
          width: px(58), height: px(8),
          backgroundColor: '#EF4444',
          borderRight: `${px(3)} solid #B91C1C`,
          borderTop: `${px(2)} solid #F87171`,
          borderRadius: px(3),
          zIndex: 10,
        }} />
      );
    case 'backpack':
      return (
        <div style={{
          position: 'absolute', bottom: px(58), left: '50%', transform: 'translateX(-50%)',
          zIndex: 1,
        }}>
          {/* Straps visible from front */}
          <div style={{
            position: 'absolute', top: px(-20), left: px(-20),
            width: px(6), height: px(24),
            backgroundColor: '#7C3AED',
            borderRadius: px(2),
          }} />
          <div style={{
            position: 'absolute', top: px(-20), right: px(-20),
            width: px(6), height: px(24),
            backgroundColor: '#7C3AED',
            borderRadius: px(2),
          }} />
        </div>
      );
    case 'guitar':
      return (
        <div style={{
          position: 'absolute', bottom: px(40), left: '50%', transform: 'translateX(-50%)',
          zIndex: 1,
        }}>
          {/* Guitar strap across chest */}
          <div style={{
            position: 'absolute', top: px(-30), left: px(-22),
            width: px(44), height: px(5),
            backgroundColor: '#8B4513',
            borderRadius: px(2),
            transform: 'rotate(-35deg)',
          }} />
        </div>
      );
    case 'earrings':
      return (
        <div style={{
          position: 'absolute', bottom: px(110), left: '50%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          <div style={{
            position: 'absolute', left: px(-34), top: 0,
            width: px(8), height: px(8),
            backgroundColor: '#FFD700',
            borderRadius: '50%',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }} />
          <div style={{
            position: 'absolute', right: px(-34), top: 0,
            width: px(8), height: px(8),
            backgroundColor: '#FFD700',
            borderRadius: '50%',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }} />
        </div>
      );
    case 'necklace':
      return (
        <div style={{
          position: 'absolute', bottom: px(72), left: '50%', transform: 'translateX(-50%)',
          zIndex: 3,
        }}>
          <div style={{
            width: px(36), height: px(16),
            border: `${px(3)} solid #FFD700`,
            borderTop: 'none',
            borderRadius: `0 0 ${px(18)} ${px(18)}`,
          }} />
          <div style={{
            position: 'absolute', bottom: px(-4), left: '50%', transform: 'translateX(-50%)',
            width: px(10), height: px(10),
            backgroundColor: '#EF4444',
            borderRadius: '50%',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }} />
        </div>
      );
    case 'scarf':
      return (
        <div style={{
          position: 'absolute', bottom: px(68), left: '50%', transform: 'translateX(-50%)',
          zIndex: 3,
        }}>
          <div style={{
            width: px(50), height: px(14),
            backgroundColor: '#EF4444',
            borderRight: `${px(3)} solid #B91C1C`,
            borderRadius: px(4),
          }} />
          <div style={{
            position: 'absolute', top: px(10), right: px(-6),
            width: px(14), height: px(28),
            backgroundColor: '#EF4444',
            borderRight: `${px(3)} solid #B91C1C`,
            borderRadius: `0 0 ${px(4)} ${px(4)}`,
          }} />
        </div>
      );
    case 'watch':
      return (
        <div style={{
          position: 'absolute', bottom: px(42), left: px(-4),
          zIndex: 10,
        }}>
          <div style={{
            width: px(12), height: px(16),
            backgroundColor: '#1F2937',
            borderRadius: px(3),
            border: `${px(2)} solid #6B7280`,
          }}>
            <div style={{
              position: 'absolute', top: px(4), left: px(3),
              width: px(4), height: px(4),
              backgroundColor: '#3B82F6',
              borderRadius: px(1),
            }} />
          </div>
        </div>
      );
    case 'wings':
      return (
        <div style={{
          position: 'absolute', bottom: px(60), left: '50%', transform: 'translateX(-50%)',
          zIndex: 0,
        }}>
          {/* Left wing */}
          <div style={{
            position: 'absolute', left: px(-50), top: 0,
            width: px(40), height: px(60),
            backgroundColor: '#E0E7FF',
            borderRadius: `${px(20)} ${px(4)} ${px(20)} ${px(20)}`,
            border: `${px(2)} solid #A5B4FC`,
            opacity: 0.9,
          }} />
          {/* Right wing */}
          <div style={{
            position: 'absolute', right: px(-50), top: 0,
            width: px(40), height: px(60),
            backgroundColor: '#E0E7FF',
            borderRadius: `${px(4)} ${px(20)} ${px(20)} ${px(20)}`,
            border: `${px(2)} solid #A5B4FC`,
            opacity: 0.9,
          }} />
        </div>
      );
    case 'halo':
      return (
        <div style={{
          position: 'absolute', bottom: px(145), left: '50%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          <div style={{
            width: px(44), height: px(12),
            backgroundColor: 'rgba(255,215,0,0.3)',
            border: `${px(4)} solid #FFD700`,
            borderRadius: '50%',
            boxShadow: '0 0 10px rgba(255,215,0,0.5)',
          }} />
        </div>
      );
    case 'wizardhat':
      return (
        <div style={{
          position: 'absolute', bottom: px(132), left: '50%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          <div style={{
            width: px(58), height: px(10),
            backgroundColor: '#4F46E5',
            borderRight: `${px(4)} solid #3730A3`,
            borderRadius: px(2),
          }} />
          <div style={{
            position: 'absolute', bottom: px(8), left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: `${px(24)} solid transparent`,
            borderRight: `${px(24)} solid transparent`,
            borderBottom: `${px(45)} solid #6366F1`,
          }} />
          <div style={{
            position: 'absolute', top: px(-30), left: '50%', transform: 'translateX(-50%)',
            width: px(8), height: px(8),
            backgroundColor: '#FFD700',
            borderRadius: '50%',
          }} />
        </div>
      );
    case 'piratehat':
      return (
        <div style={{
          position: 'absolute', bottom: px(130), left: '50%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          <div style={{
            width: px(60), height: px(12),
            backgroundColor: '#1F2937',
            borderRight: `${px(4)} solid #111827`,
            borderRadius: px(3),
          }} />
          <div style={{
            position: 'absolute', bottom: px(10), left: '50%', transform: 'translateX(-50%)',
            width: px(50), height: px(25),
            backgroundColor: '#1F2937',
            borderRight: `${px(4)} solid #111827`,
            borderRadius: `${px(12)} ${px(12)} 0 0`,
          }}>
            {/* Skull emblem */}
            <div style={{
              position: 'absolute', bottom: px(4), left: '50%', transform: 'translateX(-50%)',
              width: px(12), height: px(12),
              backgroundColor: '#FFF',
              borderRadius: '50%',
            }} />
          </div>
        </div>
      );
    case 'bunnyears':
      return (
        <div style={{
          position: 'absolute', bottom: px(138), left: '50%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          {/* Left ear - extends upward */}
          <div style={{
            position: 'absolute', left: px(-22), bottom: 0,
            width: px(14), height: px(45),
            backgroundColor: '#FFF',
            borderRight: `${px(2)} solid #E5E5E5`,
            borderRadius: `${px(8)} ${px(8)} ${px(4)} ${px(4)}`,
          }}>
            <div style={{
              position: 'absolute', top: px(6), left: px(4),
              width: px(6), height: px(28),
              backgroundColor: '#FECACA',
              borderRadius: px(3),
            }} />
          </div>
          {/* Right ear - extends upward */}
          <div style={{
            position: 'absolute', right: px(-22), bottom: 0,
            width: px(14), height: px(45),
            backgroundColor: '#FFF',
            borderRight: `${px(2)} solid #E5E5E5`,
            borderRadius: `${px(8)} ${px(8)} ${px(4)} ${px(4)}`,
          }}>
            <div style={{
              position: 'absolute', top: px(6), left: px(4),
              width: px(6), height: px(28),
              backgroundColor: '#FECACA',
              borderRadius: px(3),
            }} />
          </div>
        </div>
      );
    case 'catears':
      return (
        <div style={{
          position: 'absolute', bottom: px(148), left: '50%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          {/* Left ear - using bottom positioning to extend upward */}
          <div style={{
            position: 'absolute', left: px(-26), bottom: 0,
            width: 0, height: 0,
            borderLeft: `${px(14)} solid transparent`,
            borderRight: `${px(14)} solid transparent`,
            borderBottom: `${px(28)} solid #1F2937`,
          }}>
            <div style={{
              position: 'absolute', top: px(12), left: px(-7),
              width: 0, height: 0,
              borderLeft: `${px(7)} solid transparent`,
              borderRight: `${px(7)} solid transparent`,
              borderBottom: `${px(14)} solid #FECACA`,
            }} />
          </div>
          {/* Right ear - using bottom positioning to extend upward */}
          <div style={{
            position: 'absolute', right: px(-26), bottom: 0,
            width: 0, height: 0,
            borderLeft: `${px(14)} solid transparent`,
            borderRight: `${px(14)} solid transparent`,
            borderBottom: `${px(28)} solid #1F2937`,
          }}>
            <div style={{
              position: 'absolute', top: px(12), left: px(-7),
              width: 0, height: 0,
              borderLeft: `${px(7)} solid transparent`,
              borderRight: `${px(7)} solid transparent`,
              borderBottom: `${px(14)} solid #FECACA`,
            }} />
          </div>
        </div>
      );
    case 'santahat':
      return (
        <div style={{
          position: 'absolute', bottom: px(130), left: '50%', transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          {/* White trim */}
          <div style={{
            width: px(58), height: px(12),
            backgroundColor: '#FFF',
            borderRight: `${px(3)} solid #E5E5E5`,
            borderRadius: px(3),
          }} />
          {/* Red part */}
          <div style={{
            position: 'absolute', bottom: px(10), left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: `${px(26)} solid transparent`,
            borderRight: `${px(26)} solid transparent`,
            borderBottom: `${px(35)} solid #DC2626`,
          }} />
          {/* White pom-pom */}
          <div style={{
            position: 'absolute', top: px(-25), left: '50%', transform: 'translateX(-50%)',
            width: px(14), height: px(14),
            backgroundColor: '#FFF',
            borderRadius: '50%',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }} />
        </div>
      );
    default:
      return null;
  }
}

// Render accessories for side view
function renderAccessorySide(accessory, size, px) {
  if (accessory === 'cap') {
    return (
      <div style={{
        position: 'absolute', bottom: px(120), left: '50%', transform: 'translateX(-50%)',
        zIndex: 10,
      }}>
        <div style={{
          width: px(50), height: px(18),
          backgroundColor: '#DC2626',
          borderRight: `${px(4)} solid #B91C1C`,
          borderTop: `${px(3)} solid #EF4444`,
          borderRadius: `${px(5)} ${px(5)} 0 0`,
        }} />
        <div style={{
          position: 'absolute', bottom: px(-4), left: px(-10),
          width: px(28), height: px(8),
          backgroundColor: '#DC2626',
          borderRight: `${px(2)} solid #B91C1C`,
          borderRadius: px(4),
        }} />
      </div>
    );
  }
  if (accessory === 'glasses') {
    return (
      <div style={{
        position: 'absolute', bottom: px(96), left: px(30),
        width: px(14), height: px(12),
        backgroundColor: 'rgba(100, 149, 237, 0.3)',
        border: `${px(2)} solid #1a1a1a`,
        borderRadius: px(2),
        zIndex: 10,
      }}>
        <div style={{
          position: 'absolute', top: px(3), right: px(-18),
          width: px(20), height: px(3),
          backgroundColor: '#1a1a1a',
        }} />
      </div>
    );
  }
  if (accessory === 'sunglasses') {
    return (
      <div style={{
        position: 'absolute', bottom: px(96), left: px(28),
        width: px(16), height: px(14),
        backgroundColor: 'rgba(20, 20, 20, 0.75)',
        border: `${px(2)} solid #1a1a1a`,
        borderRadius: px(3),
        zIndex: 10,
      }}>
        <div style={{
          position: 'absolute', top: px(3), right: px(-18),
          width: px(20), height: px(3),
          backgroundColor: '#1a1a1a',
        }} />
      </div>
    );
  }
  if (accessory === 'headband') {
    return (
      <div style={{
        position: 'absolute', bottom: px(118), left: '50%', transform: 'translateX(-50%)',
        width: px(50), height: px(7),
        backgroundColor: '#EF4444',
        borderRight: `${px(3)} solid #B91C1C`,
        borderRadius: px(3),
        zIndex: 10,
      }} />
    );
  }
  if (accessory === 'backpack') {
    return (
      <div style={{
        position: 'absolute', bottom: px(50), right: px(-8),
        width: px(22), height: px(30),
        backgroundColor: '#7C3AED',
        borderRight: `${px(4)} solid #6D28D9`,
        borderTop: `${px(3)} solid #8B5CF6`,
        borderRadius: px(4),
        zIndex: 1,
      }}>
        {/* Pocket */}
        <div style={{
          position: 'absolute', bottom: px(4), left: '50%', transform: 'translateX(-50%)',
          width: px(12), height: px(10),
          backgroundColor: '#6D28D9',
          borderRadius: px(2),
        }} />
      </div>
    );
  }
  if (accessory === 'guitar') {
    return (
      <div style={{
        position: 'absolute', bottom: px(30), right: px(-14),
        zIndex: 1,
      }}>
        {/* Guitar body */}
        <div style={{
          width: px(20), height: px(24),
          backgroundColor: '#D97706',
          borderRight: `${px(3)} solid #B45309`,
          borderTop: `${px(2)} solid #FBBF24`,
          borderRadius: `${px(10)} ${px(10)} ${px(8)} ${px(8)}`,
        }}>
          <div style={{
            position: 'absolute', top: px(8), left: '50%', transform: 'translateX(-50%)',
            width: px(6), height: px(6),
            backgroundColor: '#1a1a1a', borderRadius: '50%',
          }} />
        </div>
        {/* Neck */}
        <div style={{
          position: 'absolute', top: px(-22), left: '50%', transform: 'translateX(-50%)',
          width: px(6), height: px(24),
          backgroundColor: '#92400E',
          borderRadius: px(1),
        }} />
        {/* Strap */}
        <div style={{
          position: 'absolute', top: px(-20), left: px(-12),
          width: px(30), height: px(4),
          backgroundColor: '#8B4513',
          borderRadius: px(2),
          transform: 'rotate(30deg)',
        }} />
      </div>
    );
  }
  return null;
}

export default BlockyCharacter3D;
